import { FC, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

/**
 * A block of text cut to about three lines, with a "Show more" text link under it only when there is more text
 * than that (1NOW-11384): a long operator note (headings, a "What's covered" list) no longer makes a protection
 * card very tall. The link is a real button (aria-expanded) that looks like a link; it sits above a row's tap area
 * (`relative`) and its press never reaches the option's radio. The text stays whole in the page, so the radio's
 * description and the links inside the note still work.
 *
 * `className` styles the text block (no `block`/`flex`: they would undo the clamp), `linkClassName` places the link.
 */
const ShowMore: FC<{ id?: string; className?: string; linkClassName?: string; children: ReactNode }> = ({
  id,
  className = "",
  linkClassName = "",
  children,
}) => {
  const auto = useId();
  const boxId = id ?? auto;
  const box = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);

  // Measured only while cut: opened, the box is as tall as its text. After every render too, for a new note.
  const measure = () => {
    const el = box.current;
    if (el && !open) setLong(el.scrollHeight > el.clientHeight + 1);
  };
  useLayoutEffect(measure);
  const again = useRef(measure);
  again.current = measure;
  useEffect(() => {
    // the width changes (rotating the phone, resizing the window) and the web font arrives: the lines re-wrap
    const run = () => again.current();
    window.addEventListener("resize", run);
    (document as any).fonts?.ready?.then(run);
    return () => window.removeEventListener("resize", run);
  }, []);

  return (
    <>
      <span ref={box} id={boxId} className={`${className} ${open ? "block" : "line-clamp-3"}`}>
        {children}
      </span>
      {long && (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={boxId}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
          className={`relative block text-sm font-medium text-primary-6000 underline underline-offset-2 ${linkClassName}`}
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
};

export default ShowMore;
